import { useEffect, useRef } from 'react';

const GSI_SRC = 'https://accounts.google.com/gsi/client';
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/**
 * Bouton « Se connecter avec Google » (Google Identity Services).
 * Charge le script GIS, initialise avec le client_id (public, via VITE_GOOGLE_CLIENT_ID)
 * et rend le bouton officiel. Au succès, Google renvoie un ID token → onCredential(idToken).
 * Si VITE_GOOGLE_CLIENT_ID n'est pas défini, le composant ne rend rien (feature désactivée).
 *
 * @param {(credential:string)=>void} onCredential
 * @param {string} [text] - 'continue_with' | 'signin_with' | 'signup_with'
 */
export default function GoogleSignInButton({
    onCredential,
    text = 'continue_with', // 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
    theme = 'outline',      // 'outline' | 'filled_blue' | 'filled_black'
    shape = 'pill',         // 'rectangular' | 'pill' | 'square' | 'circle'
    width = 340,            // px (max 400)
    logoAlignment = 'left', // 'left' | 'center'
}) {
    const ref = useRef(null);
    // Callback dans une ref → l'effet ne dépend pas de son identité (évite la
    // ré-initialisation de GIS à chaque render = warning « initialize() multiple times »).
    const cbRef = useRef(onCredential);
    cbRef.current = onCredential;

    useEffect(() => {
        if (!CLIENT_ID) return;
        let cancelled = false;

        const render = () => {
            if (cancelled || !window.google?.accounts?.id || !ref.current) return;
            window.google.accounts.id.initialize({
                client_id: CLIENT_ID,
                callback: (resp) => { if (resp?.credential) cbRef.current?.(resp.credential); },
            });
            ref.current.innerHTML = '';
            window.google.accounts.id.renderButton(ref.current, {
                theme,
                size: 'large',
                text,
                shape,
                logo_alignment: logoAlignment,
                width,
                locale: 'fr',
            });
        };

        if (window.google?.accounts?.id) {
            render();
            return () => { cancelled = true; };
        }

        let s = document.getElementById('google-gsi');
        if (!s) {
            s = document.createElement('script');
            s.src = GSI_SRC;
            s.async = true;
            s.defer = true;
            s.id = 'google-gsi';
            document.body.appendChild(s);
        }
        s.addEventListener('load', render);
        return () => { cancelled = true; s?.removeEventListener('load', render); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!CLIENT_ID) return null;
    return <div ref={ref} style={{ display: 'flex', justifyContent: 'center', minHeight: 40 }} />;
}
